import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HashLocationStrategy } from '@angular/common';
import { MsalModule, MsalGuard, MsalInterceptor, MsalInterceptorConfiguration, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { PublicClientApplication, BrowserCacheLocation, InteractionType } from '@azure/msal-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Angular Materials
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CommonModule, DatePipe } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MainComponent } from './main/main.component';
import { UserService } from './services/user.service';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { FooterComponent } from './footer/footer.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatBadgeModule } from '@angular/material/badge';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { RouterModule } from '@angular/router';
import { SidenavComponent } from './sidenav/sidenav.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { EventRequestComponent } from './event-request/event-request.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
//import { ProfileComponent } from './profile/profile.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AdminComponent } from './admin/admin.component';
import { FormsModule } from '@angular/forms';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UserSelectionDialogComponent } from './user-selection-dialog/user-selection-dialog.component';
import { AddRequestorDialogComponent } from './add-requestor-dialog/add-requestor-dialog.component';
import { AdminEventsComponent } from './admin-events/admin-events.component';
import { MyRequestsComponent } from './my-requests/my-requests.component';
import { EventVersionHistoryDialogComponent } from './event-version-history-dialog/event-version-history-dialog.component';
import { EventHistoryTableComponent } from './event-history/event-history-table.component';
import { CsvUploadDialogComponent } from './csv-upload-dialog/csv-upload-dialog.component';
import { EventMetricsDialogComponent } from './event-metrics-dialog/event-metrics-dialog.component';
import { HealthComponent } from './health/health.component';
import { BlockedUsersComponent } from './blocked-users/blocked-users.component';
import { MatDialogModule } from '@angular/material/dialog';
import { DatabaseService } from './services/database.service';
import { AlertComponent } from './alert/alert.component';
import { AlertService } from './services/alert.service';
import { BackendAuthInterceptor } from './services/backend-auth.interceptor';


const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    MainComponent,
    SidenavComponent,
    DashboardComponent,
    EventRequestComponent,
    AdminComponent,
    AdminEventsComponent,
    MyRequestsComponent,
    UserSelectionDialogComponent,
    AlertComponent,
    AddRequestorDialogComponent,
    EventVersionHistoryDialogComponent,
    EventHistoryTableComponent,
    CsvUploadDialogComponent,
    EventMetricsDialogComponent,
    HealthComponent,
    BlockedUsersComponent
  ],
  imports: [
    MatSelectModule,
    MatCheckboxModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    //ProfileComponent,
    MatSidenavModule,
    MatBadgeModule,
    MatToolbarModule,
    MatButtonModule,
    MatListModule,
    MatSelectModule,
    MatTooltipModule,
    MatCardModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSidenavModule,
    FormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    BrowserModule,
    MatDatepickerModule,
    MatTimepickerModule,
    RouterModule,
    HttpClientModule,
    MatIconModule,
    BrowserModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressBarModule,
    CommonModule,
    MsalModule.forRoot(new PublicClientApplication({
        auth: {
            clientId: environment.msal.clientId,
            redirectUri: '/',
            authority: environment.msal.authority
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: isIE,
        },
    }), {
        interactionType: InteractionType.Redirect,
        authRequest: {
            // Request scopes for both Graph API and Backend API during initial login
            scopes: [
                "User.Read", 
                "User.ReadBasic.All",
                environment.msal.backendApiScope  // Backend API access
            ]
        }
    }, {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: environment.msal.protectedResourceMap
    }),
    MatCheckboxModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatNativeDateModule,
    MatInputModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    FormsModule,
    MatFormFieldModule
],
  providers: [
    provideAnimationsAsync(),
    UserService,
    DatabaseService,
    AlertService,
    MatDatepickerModule,
    MatTimepickerModule,
    { provide: HashLocationStrategy, useClass: HashLocationStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BackendAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
  MsalGuard,
  DatePipe
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private matIconRegistry: MatIconRegistry, private domSanitizer: DomSanitizer) {
    this.matIconRegistry.addSvgIcon(
      "backIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/arrow_back.svg')
    );
    this.matIconRegistry.addSvgIcon(
      "copyIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/copy_link.svg')
    );
  }
}
